import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { FabricCanvas } from '../../types/fabric';
import { SearchableSelect } from '../SearchableSelect';
import { Skeleton } from '../Skeleton';
import { useToast } from '../Notifications';
import { BackgroundColorTool } from './BackgroundColorTool';
import { importCatalogAssetAsUpload, type StagedImage } from './StagedImage';
import { Spinner } from '../Spinner';
import { ToolSection } from './shared/ToolSection';

interface ClipartToolProps {
  canvas: FabricCanvas | null;
}

export function ClipartTool({ canvas }: ClipartToolProps) {
  const { t } = useTranslation();
  const showToast = useToast();
  const [selectedCat, setSelectedCat] = useState<string>('');
  const [page, setPage] = useState(1);
  // Accumulated across pages — "Load more" must append, not replace, the grid.
  const [allImages, setAllImages] = useState<any[]>([]);
  // Tracks which grid thumbnails have actually finished loading, so each cell
  // can show a skeleton placeholder until then instead of a blank/jumping grid.
  const [loadedIds, setLoadedIds] = useState<Set<string>>(new Set());
  // Same staged-then-wizard flow as UploadTool.tsx, per direct user request
  // that picking a clipart go through the same vectorize/remove-background/
  // select-colors/crop steps a direct file upload does, instead of dropping
  // straight onto the canvas.
  const [staged, setStaged] = useState<StagedImage | null>(null);
  const [importingId, setImportingId] = useState<string | null>(null);

  const { data: catData } = useQuery({
    queryKey: ['clipart-categories'],
    queryFn:  () => fetch('/api/cliparts/categories').then(r => r.json()),
    staleTime: 300_000,
  });

  const { data: clipData, isFetching: clipartsLoading } = useQuery({
    queryKey: ['cliparts', selectedCat, page],
    queryFn:  () => selectedCat ? fetch(`/api/cliparts/${selectedCat}?page=${page}`).then(r => r.json()) : null,
    enabled:  !!selectedCat,
    staleTime: 60_000,
  });

  // Reset accumulated images whenever the category changes (page already resets via onChange)
  useEffect(() => { setAllImages([]); setLoadedIds(new Set()); }, [selectedCat]);

  useEffect(() => {
    if (!clipData?.clipart_images) return;
    setAllImages(prev => page === 1 ? clipData.clipart_images : [...prev, ...clipData.clipart_images]);
  }, [clipData, page]);

  const markLoaded = (id: string) => setLoadedIds(prev => (prev.has(id) ? prev : new Set(prev).add(id)));

  const selectClipart = async (img: any) => {
    setImportingId(img.image_id);
    try {
      const result = await importCatalogAssetAsUpload(img.image_path);
      if (!result) {
        showToast(t('clipart.importFailed'), 'error');
        return;
      }
      setStaged(result);
    } finally {
      setImportingId(null);
    }
  };

  const categories = catData?.tree ?? [];
  const categoryOptions = categories.map((c: any) => ({ value: c.id, label: c.clipartTitle ?? c.name }));

  if (staged) {
    return (
      <div style={{ padding: 12 }}>
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, background: '#f8fafc' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>{t('clipart.editingClipart')}</span>
            <button onClick={() => setStaged(null)} title={t('upload.cancelUpload')}
              style={{ border: 0, background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 2 }}>
              ×
            </button>
          </div>
          {/* key={staged.uploadId} forces a fresh wizard instance per clipart —
              same reasoning as UploadTool.tsx's identical key. */}
          <BackgroundColorTool key={staged.uploadId} canvas={canvas} staged={staged} onDone={() => setStaged(null)} />
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 12, display: 'flex', flexDirection: 'column' }}>
      <ToolSection title={t('clipart.sectionCategory')} divider={false}>
        <SearchableSelect
          options={categoryOptions}
          value={selectedCat}
          onChange={value => { setSelectedCat(value); setPage(1); }}
          placeholder={t('clipart.selectCategory')}
          searchPlaceholder={t('clipart.searchCategory')}
          noMatchesLabel={t('clipart.noMatches')}
        />
      </ToolSection>

      <ToolSection title={t('clipart.sectionBrowse')}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
          {allImages.map((img: any) => (
            <button key={img.image_id} onClick={() => selectClipart(img)} disabled={importingId !== null}
              style={{ position: 'relative', border: '1px solid var(--border)', borderRadius: 4, background: 'none', cursor: importingId !== null ? 'default' : 'pointer', padding: 4, overflow: 'hidden', aspectRatio: '1' }}>
              {!loadedIds.has(img.image_id) && <Skeleton style={{ position: 'absolute', inset: 4 }} />}
              {/* `thumb_url` is a small pre-generated WebP — used here for grid
                  speed; the wizard's staged preview (above) uses the full
                  `image_path` SVG. */}
              <img
                src={img.thumb_url ?? img.image_path}
                alt={img.label}
                style={{ width: '100%', height: '100%', objectFit: 'contain', visibility: loadedIds.has(img.image_id) ? 'visible' : 'hidden' }}
                loading="lazy"
                onLoad={() => markLoaded(img.image_id)}
                onError={() => markLoaded(img.image_id)}
              />
              {importingId === img.image_id && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.7)' }}>
                  <Spinner size={18} />
                </div>
              )}
            </button>
          ))}
          {clipartsLoading && page === 1 && allImages.length === 0 &&
            Array.from({ length: 9 }).map((_, i) => (
              <div key={`skeleton-${i}`} style={{ border: '1px solid var(--border)', borderRadius: 4, overflow: 'hidden', aspectRatio: '1', padding: 4, boxSizing: 'border-box' }}>
                <Skeleton />
              </div>
            ))
          }
        </div>

        {clipData?.loadMoreFlag === 1 && (
          <button onClick={() => setPage(p => p + 1)} className="btn btn-secondary" style={{ width: '100%', fontSize: 12 }} disabled={clipartsLoading}>
            {clipartsLoading ? t('clipart.loading') : t('clipart.loadMore')}
          </button>
        )}
      </ToolSection>
    </div>
  );
}
